import { useEffect, useRef, useState } from 'react';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { travelSafetyApi } from '../../api/travelSafetyApi';
import {
  MEMBER_REPORT_REASONS,
  REPORT_NOTE_MAX_LENGTH,
  newSafetyClientRequestId,
  safetyErrorMessage,
} from '../group-trip/memberSafetyModel';
import { useMemberSafetyDialogA11y } from '../group-trip/memberSafetyDialogA11y';
import '../group-trip/group-trip.css';

const ALLOWED_TARGETS = new Set([
  'SOCIAL_POST',
  'COMMENT',
  'MEMBER',
  'JOURNEY_PUBLICATION',
  'GROUP_TRIP',
]);

/**
 * Content report dialog — reuses MEMBER report reasons + existing reports API (AUTH-35 / B5-2).
 * Must send targetType + targetId correctly (never silently coerce Post/GroupTrip → MEMBER).
 * targetId is an opaque string (snowflake-safe); do not Number()-coerce.
 */
export default function ContentReportDialog({
  open,
  targetType,
  targetId,
  titleKey = 'travel.safety.reportContent',
  onClose,
  onReported,
}) {
  const { t } = useTravelTranslation();
  const [reason, setReason] = useState(MEMBER_REPORT_REASONS[0]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const requestIdRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setReason(MEMBER_REPORT_REASONS[0]);
    setNote('');
    setBusy(false);
    setError('');
    setDone(false);
    requestIdRef.current = newSafetyClientRequestId('report');
    return undefined;
  }, [open, targetType, targetId]);

  useMemberSafetyDialogA11y({ open, onClose, busy, panelRef });

  if (!open) return null;

  async function submit(event) {
    event.preventDefault();
    if (busy || done || targetId == null || targetId === '') return;
    if (!ALLOWED_TARGETS.has(targetType)) {
      setError(t('travel.safety.reportFailed'));
      return;
    }
    if (!requestIdRef.current) {
      requestIdRef.current = newSafetyClientRequestId('report');
    }
    setBusy(true);
    setError('');
    try {
      await travelSafetyApi.report({
        targetType,
        targetId,
        reason,
        note: note.trim() || null,
        clientRequestId: requestIdRef.current,
      });
      setDone(true);
      onReported?.();
    } catch (cause) {
      setError(safetyErrorMessage(cause, t, 'travel.safety.reportFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="member-safety-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="content-report-title"
      data-testid="content-report-dialog"
      onClick={() => { if (!busy) onClose?.(); }}
    >
      <div
        className="member-safety-dialog__panel"
        ref={panelRef}
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id="content-report-title">{t(titleKey)}</h3>
        {done ? (
          <>
            <p>{t('travel.safety.reportSubmitted')}</p>
            <button type="button" onClick={onClose}>{t('travel.safety.close')}</button>
          </>
        ) : (
          <form onSubmit={submit}>
            <label className="member-safety-dialog__field">
              {t('travel.safety.reason')}
              <select value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy}>
                {MEMBER_REPORT_REASONS.map((value) => (
                  <option key={value} value={value}>
                    {t(`travel.safety.reasons.${value}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="member-safety-dialog__field">
              {t('travel.safety.optionalNote')}
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, REPORT_NOTE_MAX_LENGTH))}
                maxLength={REPORT_NOTE_MAX_LENGTH}
                rows={3}
                disabled={busy}
              />
            </label>
            {error ? <p className="group-trips__error" role="alert">{error}</p> : null}
            <div className="group-trips__actions">
              <button type="submit" disabled={busy} data-testid="content-report-submit">
                {busy ? t('travel.safety.submitting') : t('travel.safety.submitReport')}
              </button>
              <button type="button" disabled={busy} onClick={onClose}>{t('travel.safety.cancel')}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
